import $ from 'jquery';
import debounce from 'lodash/debounce';
import { AnyAction, Middleware } from '@reduxjs/toolkit';
import {
  loginButtonClick,
  sessionValid,
  loginInProgress,
  setCookieCheckIntervalId,
  selectIsSessionValid,
  selectUserSessionOptions,
  selectCookieCheckIntervalId,
} from 'store/reducers/UserSessionReducer';
import type { StorytellerState } from 'store/StorytellerReduxState';

let lastCookie = document.cookie;

export const userSessionMiddleware: Middleware<StorytellerState> = (store) => {
  // Handle session validation with debounce to prevent rapid-fire requests
  const checkUserSession = debounce(() => {
    $.get('/api/users/current.json').then(
      function success() {
        if (!selectIsSessionValid(store.getState())) {
          store.dispatch(sessionValid(true));
        }
        if (store.getState().userSession.loginInProgress) {
          store.dispatch(loginInProgress(false));
        }
      },
      function error() {
        if (selectIsSessionValid(store.getState())) {
          store.dispatch(sessionValid(false));
        }
      },
    );
  }, selectUserSessionOptions(store.getState()).sessionCheckDebounceMilliseconds);

  // Set up AJAX error handling
  $(document).ajaxError((event, jqXhr) => {
    if (jqXhr.status === 401 || jqXhr.status === 403) {
      checkUserSession();
    }
  });

  // Start the cookie check interval
  const options = selectUserSessionOptions(store.getState());
  const intervalId = setInterval(() => {
    if (document.cookie !== lastCookie) {
      lastCookie = document.cookie;
      checkUserSession();
    }
  }, options.cookieCheckIntervalMilliseconds);

  // Store the interval ID in the Redux state for potential cleanup
  // Defer the dispatch until after middleware initialization
  // to avoid "Dispatching while constructing your middleware is not allowed" error
  setTimeout(() => {
    store.dispatch(setCookieCheckIntervalId(intervalId));
  }, 0);

  return (next) => (action: AnyAction) => {
    // Handle login button click action
    if (loginButtonClick.match(action)) {
      const state = store.getState();
      if (!state.userSession.loginInProgress) {
        checkUserSession();
        // We need to set this synchronously because of browser popup behavior (comment copied from original UserSessionStore)
        store.dispatch(loginInProgress(true));
      }
    }

    return next(action);
  };
};

// Export a cleanup function that can be used in tests or when the app is unmounted
export const cleanupUserSessionMiddleware = (store: any) => {
  const intervalId = selectCookieCheckIntervalId(store.getState());
  if (intervalId) {
    clearInterval(intervalId);
    store.dispatch(setCookieCheckIntervalId(null));
  }
  $(document).off('ajaxError');
};
